import React, {FC, useMemo} from 'react';
import {BucketInfo} from "interfaces";
import {Accordion} from "react-bootstrap";
import {toDateString, toDayDateString} from "utils";
import {ModerationSection} from "components/ModerationSection";

type GroupByDateResult = {
    [date: string]: number;
};

type GroupedDay = { label: string; stamp: number; count: number };

interface Props {
    label: string;
    buckets: BucketInfo<Date>[];
    type: "sparks" | "boosts";
}

export const ModerationBuckets: FC<Props> = ({label, buckets, type}) => {
    const sortedBuckets = useMemo(() => {
        return [...buckets].sort((a, b) => {
            const aT = a.date ? new Date(a.date).getTime() : 0
            const bT = b.date ? new Date(b.date).getTime() : 0
            return bT - aT
        })
    }, [buckets])

    const totals = useMemo(() => {
        return sortedBuckets.reduce(
            (acc, b) => {
                acc.total += b.usageTimes.length ?? 0
                acc.unused += b.unusedCount ?? 0
                acc.used += Math.max(0, (b.usageTimes.length ?? 0) - (b.unusedCount ?? 0))
                return acc
            },
            {total: 0, unused: 0, used: 0},
        )
    }, [sortedBuckets])

    if (!buckets.length) {
        return null
    }

    const groupByDay = (dates: Date[]): GroupedDay[] => {
        const byStamp = new Map<number, { label: string; count: number }>();

        for (const d of dates) {
            const stamp = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
            const label = toDayDateString(d, false) ?? 'unknown';
            const prev = byStamp.get(stamp);
            byStamp.set(stamp, {label, count: (prev?.count ?? 0) + 1});
        }

        return [...byStamp.entries()]
            .map(([stamp, v]) => ({stamp, label: v.label, count: v.count}))
            .sort((a, b) => b.stamp - a.stamp);
    };

    return <ModerationSection label={label}>
        <Accordion key={label} flush alwaysOpen={false} className={'w-100'}>
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <span className="d-flex flex-wrap align-items-center" style={{gap: 8}}>
                        <span className="fw-semibold">История</span>
                        <span className="badge text-bg-secondary" title="Всего использовано">
                            Всего использовано {totals.used}
                        </span>
                        <span className="badge text-bg-primary" title="Доступно сейчас">
                            Доступно сейчас {totals.unused}
                        </span>
                    </span>
                </Accordion.Header>
                <Accordion.Body>
                    <div className="table-responsive">
                        <table className="table table-sm table-striped align-middle mb-0">
                            <thead>
                            <tr>
                                <th scope="col" className="fw-semibold">Тип</th>
                                <th scope="col" className="fw-semibold text-nowrap">Начисление</th>
                                <th scope="col" className="fw-semibold">Следующее</th>
                                <th scope="col" className="fw-semibold text-nowrap">Доступно</th>
                                <th scope="col" className="fw-semibold">Использования</th>
                                {type === 'boosts' &&
                                    <th scope="col" className="fw-semibold text-nowrap">Длительность (мин)</th>}
                            </tr>
                            </thead>
                            <tbody>
                            {sortedBuckets.map((bucket, i) => {
                                const minsDiff = Math.round((bucket.activeTimeInSeconds ?? 0) / 60);
                                const usageTimes = bucket.usageTimes ?? [];
                                const groupedUsageDates = groupByDay(usageTimes);
                                const usageTotal = usageTimes.length;
                                const lastUsage =
                                    usageTimes.length > 0
                                        ? usageTimes.reduce((max, cur) =>
                                            cur.getTime() > max.getTime() ? cur : max,
                                        )
                                        : null;

                                return (
                                    <tr key={`${bucket.date?.toString() ?? "unknown"}_${i}`}>
                                        <td className="text-nowrap">{bucket.type}</td>
                                        <td className="text-nowrap">
                                            {bucket.date ? toDateString(bucket.date, true) : "—"}
                                        </td>
                                        <td className="text-nowrap">
                                            {bucket.expiresDate ? toDateString(bucket.expiresDate, true) : "—"}
                                        </td>

                                        <td className="text-nowrap">
                                            <span
                                                className="fw-semibold">{bucket.unusedCount}/{bucket.totalCount}</span>
                                        </td>
                                        <td>
                                            {groupedUsageDates.length === 0 ? (
                                                <span className="text-muted">—</span>
                                            ) : (
                                                <details>
                                                    <summary className="text-body" style={{cursor: "pointer"}}>
                                                        <span className="fw-semibold">{usageTotal}</span>{" "}
                                                        <span className="text-muted">исп.</span>
                                                        {lastUsage && (
                                                            <>
                                                                <span className="text-muted"> · </span>
                                                                <span className="text-muted">
                                                                    последняя: {toDayDateString(lastUsage, false)}
                                                                </span>
                                                            </>
                                                        )}
                                                    </summary>
                                                    <div className="d-flex flex-wrap mt-2" style={{gap: 6}}>
                                                        {groupedUsageDates.slice(0, 30).map((d) => (
                                                            <span
                                                                key={d.stamp}
                                                                className="badge rounded-pill text-bg-light border"
                                                            >
                                                                {d.label}{" "}
                                                                <span className="text-muted">×{d.count}</span>
                                                            </span>
                                                        ))}
                                                        {groupedUsageDates.length > 30 && (
                                                            <span className="badge rounded-pill text-bg-secondary">
                                                                +{groupedUsageDates.length - 30} дней
                                                            </span>
                                                        )}
                                                    </div>
                                                </details>
                                            )}
                                        </td>
                                        {type === 'boosts' && <td className="text-nowrap">{minsDiff}</td>}
                                    </tr>
                                )
                            })}
                            </tbody>
                        </table>
                    </div>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    </ModerationSection>
}
